// frontend/smartdocs-admin/src/components/settings/NotificationSettings.tsx
import React, { useState } from 'react';
import { useSettings } from '@/context/SettingsContext';
import ToggleSwitch from '@shared/ToggleSwitch';
import { toast } from 'react-hot-toast';
import SettingsPage from "@/pages/SettingsPage";
import Card from "@shared/Card";
import Input from "@shared/Input";
import Button from "@shared/Button";

const NotificationSettings: React.FC = () => {
  const { settings, updateSetting, loading, testEmailSettings } = useSettings();
  const [testEmailRecipient, setTestEmailRecipient] = useState('');
  const [isSendingTestEmail, setIsSendingTestEmail] = useState(false);

  const notificationSettings = settings.Notifications || {};

  const handleUpdate = (key: string, value: any) => {
    updateSetting('Notifications', key, value);
  };

  const handleTestEmail = async () => {
    if (!testEmailRecipient) {
      toast.error('Please enter a recipient email address.');
      return;
    }
    setIsSendingTestEmail(true);
    const success = await testEmailSettings(
      testEmailRecipient,
      'SmartDocs AI Test Email',
      'This is a test email from your SmartDocs AI system settings.'
    );
    if (success) {
      toast.success('Test email sent! Check your inbox.');
    } else {
      toast.error('Failed to send test email. Check server logs and SMTP settings.');
    }
    setIsSendingTestEmail(false);
  };

  return (
    <SettingsPage>
      <Card>
        <div className="space-y-6">
      {/*<h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Notification Settings</h2>*/}

      {/* Enable Email Notifications */}
      <div className="flex items-center justify-between">
        <div>
          <label htmlFor="enableEmailNotifications" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Enable Email Notifications</label>
          <p className="text-sm text-gray-500 dark:text-gray-400">Send email alerts for important system events and user actions.</p>
        </div>
        <ToggleSwitch
          id="enableEmailNotifications"
          label=""
          checked={notificationSettings.enableEmailNotifications || false}
          onChange={(e) => handleUpdate('enableEmailNotifications', e.target.checked)}
          disabled={loading}
        />
      </div>

      {/* Notification Email Sender */}
      <div>
        <label htmlFor="senderEmail" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Sender Email Address</label>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">The email address from which system notifications will be sent.</p>
        <Input
          type="email"
          id="senderEmail"
          className="p-2 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          value={notificationSettings.senderEmail || 'noreply@smartdocs.ai'}
          onChange={(e) => handleUpdate('senderEmail', e.target.value)}
          disabled={loading}
        />
      </div>

      {/* Test Email Functionality */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Test Email Configuration</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Send a test email to verify your SMTP settings are correctly configured.</p>
        <div className="flex flex-col sm:flex-row gap-4">
          <Input
            type="email"
            placeholder="Recipient Email"
            className="p-2 flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            value={testEmailRecipient}
            onChange={(e) => setTestEmailRecipient(e.target.value)}
            disabled={loading || isSendingTestEmail}
          />
          <Button
            onClick={handleTestEmail}
            className="flex items-center"
            disabled={loading || isSendingTestEmail}
            variant="primary"
          >
            {isSendingTestEmail ? 'Sending...' : 'Send Test Email'}
          </Button>
        </div>
      </div>
    </div>
      </Card>
    </SettingsPage>
  );
};

export default NotificationSettings;
